import { useMemo, useState } from 'react';
import {
  Box,
  Alert,
  AlertTitle,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Skeleton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Button,
  Tooltip,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { formatCurrency } from '@/utils/misc';
import { getInvestmentCountryLabel } from '@/features/investments/countries';
import { useGetInvestment } from '@/lib/api/investments';
import { useListInvestmentTransactions } from '@/lib/api/investment-transactions';
import { useListInvestmentRentalPeriods } from '@/lib/api/investment-rental-periods';
import {
  IRPF_BUCKETS,
  aggregateFiscal,
  getAvailableYears,
  pickDefaultYear,
  BucketAggregate,
} from './fiscal/fiscalCategories';
import { exportFiscalXLSX } from './fiscal/exporters';

interface FiscalTabProps {
  investmentId: string;
}

const DISCLAIMER_BODY =
  'Organiza tus ingresos y gastos en las categorías que usa tu gestor para el IRPF. Faltan partidas que solo tu asesor puede calcular correctamente (como la amortización o la reducción por vivienda habitual). Úsalo para ahorrar tiempo en la declaración, no para sustituirla.';

const formatTxDate = (iso: string) =>
  new Date(iso).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });

export const FiscalTab = ({ investmentId }: FiscalTabProps) => {
  const { data: investment } = useGetInvestment(investmentId);
  const { data: transactions, isLoading } = useListInvestmentTransactions(investmentId);
  const { data: rentalPeriods, isLoading: isLoadingRentalPeriods } =
    useListInvestmentRentalPeriods(investmentId);

  const [disclaimerOpen, setDisclaimerOpen] = useState(true);

  const availableYears = useMemo(() => getAvailableYears(transactions ?? []), [transactions]);
  const currentYear = new Date().getUTCFullYear();
  const defaultYear = useMemo(
    () => pickDefaultYear(availableYears, currentYear),
    [availableYears, currentYear]
  );
  const [selectedYear, setSelectedYear] = useState<number | undefined>(undefined);
  const effectiveYear = selectedYear ?? defaultYear;

  const aggregate = useMemo(
    () =>
      effectiveYear !== undefined && transactions
        ? aggregateFiscal(transactions, effectiveYear)
        : undefined,
    [transactions, effectiveYear]
  );

  const tenants = useMemo(
    () =>
      rentalPeriods?.flatMap((period) =>
        period.tenants.map((tenant) => ({
          ...tenant,
          startDate: period.startDate,
          endDate: period.endDate,
        }))
      ) ?? [],
    [rentalPeriods]
  );

  if (investment && investment.country !== 'ES') {
    return (
      <Box>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Resumen fiscal
        </Typography>
        <Alert severity="info">
          El resumen fiscal actual está preparado para inmuebles en España. Este activo está
          registrado en {getInvestmentCountryLabel(investment.country)}, por lo que sus datos se
          mantienen independientes del bloque fiscal español.
        </Alert>
      </Box>
    );
  }

  if (isLoading || isLoadingRentalPeriods || !transactions || !rentalPeriods) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={64} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={120} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={240} />
      </Box>
    );
  }

  if (transactions.length === 0) {
    return (
      <Box>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Resumen fiscal
        </Typography>
        <Alert severity="info">
          No hay transacciones registradas. Añade ingresos y gastos en la pestaña Transacciones para
          generar el resumen fiscal.
        </Alert>
      </Box>
    );
  }

  const exportCtx =
    aggregate && investment
      ? {
          investmentName: investment.alias || investment.address,
          investmentIdentificationCode: investment.identificationCode,
          investmentAddress: `${investment.address}, ${investment.city}`,
          tenants,
          year: aggregate.year,
          aggregate,
          transactions,
        }
      : undefined;

  const handleXlsx = () => {
    if (exportCtx) exportFiscalXLSX(exportCtx);
  };

  const incomeBuckets = IRPF_BUCKETS.filter((b) => b.kind === 'income');
  const deductibleBuckets = IRPF_BUCKETS.filter((b) => b.kind === 'deductible');
  const nonDeductibleBuckets = IRPF_BUCKETS.filter((b) => b.kind === 'non-deductible');
  const separateBuckets = IRPF_BUCKETS.filter((b) => b.kind === 'separate');
  const uncategorizedBucket = IRPF_BUCKETS.find((b) => b.kind === 'uncategorized');

  const renderBucket = (entry: BucketAggregate) => {
    if (entry.transactions.length === 0) return null;
    const isUncategorized = entry.bucket.kind === 'uncategorized';
    return (
      <Accordion key={entry.bucket.id} disableGutters sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', gap: 1.5 }}>
            {isUncategorized && <WarningAmberIcon color="warning" fontSize="small" />}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {entry.bucket.label}
                {entry.bucket.tooltip && (
                  <Tooltip title={entry.bucket.tooltip} arrow>
                    <InfoOutlinedIcon
                      fontSize="small"
                      sx={{ ml: 0.5, color: 'text.secondary', verticalAlign: 'middle' }}
                    />
                  </Tooltip>
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {entry.bucket.subtitle}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {formatCurrency(entry.total)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {entry.transactions.length}{' '}
                {entry.transactions.length === 1 ? 'transacción' : 'transacciones'}
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <List dense disablePadding>
            {entry.transactions
              .slice()
              .sort((a, b) => (a.date < b.date ? 1 : -1))
              .map((tx) => (
                <ListItem key={`${tx.id}-${tx.category}`} disableGutters divider>
                  <ListItemText
                    primary={tx.concept || 'Sin concepto'}
                    secondary={formatTxDate(tx.date)}
                  />
                  <Typography variant="body2" fontWeight={500}>
                    {formatCurrency(tx.amount)}
                  </Typography>
                </ListItem>
              ))}
          </List>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Resumen fiscal
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Agrupa ingresos y gastos por categoría de IRPF para facilitar la declaración.
          </Typography>
        </Box>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="fiscal-year-label">Ejercicio fiscal</InputLabel>
          <Select
            labelId="fiscal-year-label"
            label="Ejercicio fiscal"
            value={effectiveYear ?? ''}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          >
            {availableYears.map((y) => (
              <MenuItem key={y} value={y}>
                {y === currentYear ? `${y} (en curso)` : y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {disclaimerOpen && (
        <Alert severity="info" onClose={() => setDisclaimerOpen(false)} sx={{ mb: 3 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>Este resumen no es un cálculo fiscal.</AlertTitle>
          {DISCLAIMER_BODY}
        </Alert>
      )}

      {aggregate?.allUncategorized && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Tus transacciones no tienen categoría asignada. Ve a Transacciones y clasifícalas para ver
          el resumen fiscal correctamente.
        </Alert>
      )}

      {!aggregate || !aggregate.hasAnyTransactions ? (
        <Alert severity="info">
          No hay transacciones registradas para {effectiveYear}. Añade ingresos y gastos en la
          pestaña Transacciones para generar el resumen fiscal.
        </Alert>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Ingresos íntegros
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={600} color="success.main">
                    {formatCurrency(aggregate.totals.grossIncome)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <TrendingDownIcon color="error" sx={{ mr: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Gastos deducibles
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={600} color="error.main">
                    {formatCurrency(aggregate.totals.deductibleExpenses)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <AccountBalanceWalletIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Saldo organizativo
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={600}>
                    {formatCurrency(aggregate.totals.organizationalBalance)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    No es el rendimiento neto fiscal
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Ingresos
            </Typography>
            {incomeBuckets.some((b) => aggregate.byBucket[b.id].transactions.length > 0) ? (
              incomeBuckets.map((b) => renderBucket(aggregate.byBucket[b.id]))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No hay ingresos registrados en {aggregate.year}.
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Gastos deducibles
            </Typography>
            {deductibleBuckets.some((b) => aggregate.byBucket[b.id].transactions.length > 0) ? (
              deductibleBuckets.map((b) => renderBucket(aggregate.byBucket[b.id]))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No hay gastos deducibles registrados en {aggregate.year}.
              </Typography>
            )}
          </Box>

          {nonDeductibleBuckets.some((b) => aggregate.byBucket[b.id].transactions.length > 0) && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  No deducible en IRPF
                </Typography>
                {nonDeductibleBuckets.map((b) => renderBucket(aggregate.byBucket[b.id]))}
              </Box>
            </>
          )}

          {separateBuckets.some((b) => aggregate.byBucket[b.id].transactions.length > 0) && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Revisar fuera del alquiler
                </Typography>
                {separateBuckets.map((b) => renderBucket(aggregate.byBucket[b.id]))}
              </Box>
            </>
          )}

          {uncategorizedBucket &&
            aggregate.byBucket[uncategorizedBucket.id].transactions.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Sin categoría
                  </Typography>
                  {renderBucket(aggregate.byBucket[uncategorizedBucket.id])}
                </Box>
              </>
            )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleXlsx}
              disabled={!exportCtx}
            >
              Exportar resumen (XLSX)
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};
